/**
 * Typed error envelope for the animachord REST + WebSocket clients.
 *
 * Mirrors `backend-contracts.md` §0.3. The server returns the same shape
 * for every non-2xx response so callers can switch on a stable `code`
 * without per-endpoint special cases. `code` is reserved-vocabulary
 * (`not_authenticated`, `forbidden`, `not_found`, `rate_limited`, etc.)
 * — never branch on `message`.
 */

/** Wire-format envelope (snake_case) returned by the server. */
export interface ApiErrorEnvelope {
  readonly error: string;
  readonly message: string;
  readonly details?: Record<string, unknown>;
  readonly request_id: string;
  readonly retry_after_seconds?: number;
}

/**
 * Thrown by `apiClient` whenever a response is non-2xx OR the body fails
 * to parse as JSON. Carries the request id for correlation with server
 * logs and the HTTP status for callers that want a coarse retry decision.
 */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly requestId: string;
  readonly details?: Record<string, unknown>;
  readonly retryAfterSeconds?: number;

  constructor(args: {
    code: string;
    status: number;
    requestId: string;
    message: string;
    details?: Record<string, unknown>;
    retryAfterSeconds?: number;
  }) {
    super(args.message);
    this.name = "ApiError";
    this.code = args.code;
    this.status = args.status;
    this.requestId = args.requestId;
    this.details = args.details;
    this.retryAfterSeconds = args.retryAfterSeconds;
  }
}

/** Type guard: narrows `unknown` to `ApiError`. */
export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError;
}

/**
 * Type guard: matches `ApiError` whose `code` is one of the supplied
 * reserved codes. Useful for callers that want to handle `not_found` and
 * `forbidden` differently without `instanceof` plus string comparison.
 */
export function isApiErrorWithCode<C extends string>(
  err: unknown,
  ...codes: readonly C[]
): err is ApiError & { code: C } {
  return isApiError(err) && (codes as readonly string[]).includes(err.code);
}

/** Reserved error codes from `backend-contracts.md` §0.3. */
export const RESERVED_ERROR_CODES = [
  "not_authenticated",
  "forbidden",
  "not_found",
  "agent_not_found",
  "score_not_found",
  "take_not_found",
  "rate_limited",
  "quota_exceeded",
  "validation_failed",
  "idempotency_conflict",
  "internal_error",
] as const;

export type ReservedErrorCode = (typeof RESERVED_ERROR_CODES)[number];
